import { useState } from "react";
import type { ConnectorDTO, SubjectDTO, SubjectTypeDTO } from "../../shared/types";
import { del, put } from "../lib/api";
import { scopeName, toolName } from "../lib/scopes";
import { ToolMark } from "./ScopeTypeForm";
import { List, ListRow } from "./List";
import { confirmDialog } from "./Dialog";
import { Badge, Button, ErrorText, inputCls } from "./ui";

/** A scope's id in each of its type's tools: one row per tool, each set, changed or removed on its own. */
export function ScopeIds({ scope, type, connectors, onChanged }: { scope: SubjectDTO; type: SubjectTypeDTO; connectors: ConnectorDTO[]; onChanged: (scope: SubjectDTO) => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // An id kept in a tool the type no longer lists still shows, so it can be removed.
  const stray = Object.keys(scope.links).filter((system) => !type.fields.some((f) => f.system === system));
  const rows = [...type.fields.map((f, i) => ({ system: f.system, idField: f.idField as string | null, main: i === 0 })), ...stray.map((system) => ({ system, idField: null, main: false }))];

  const run = async (fn: () => Promise<SubjectDTO>) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await fn());
      setEditing(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const link = (system: string) => `/api/subjects/${scope.id}/links/${encodeURIComponent(system)}`;

  return (
    <List
      title="Ids in each tool"
      hint="Agents working under it are shown the ids in the tools they have."
      empty="Its type has no tools, so it holds a name only."
      footer={error && <ErrorText error={error} />}
    >
      {rows.map((r) => {
        const id = scope.links[r.system];
        return (
          <ListRow
            key={r.system}
            leading={
              <span className="grid size-8 place-items-center rounded-full bg-raised shadow-sm ring-1 ring-line">
                <ToolMark system={r.system} connectors={connectors} />
              </span>
            }
            title={
              <>
                <span className="truncate text-sm font-medium">{toolName(connectors, r.system)}</span>
                {r.main && <Badge tone="blue">Main tool</Badge>}
              </>
            }
            subtitle={<span className="font-mono text-xs">{r.idField ?? "no longer a tool of this type"}</span>}
            trailing={
              editing !== r.system && (
                <span className="flex">
                  {r.idField !== null && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setEditing(r.system);
                        setValue(id ?? "");
                      }}
                    >
                      {id ? "Edit" : "Add"}
                    </Button>
                  )}
                  {id && (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      onClick={async () =>
                        (await confirmDialog({ title: `Remove the ${toolName(connectors, r.system)} id of ${scopeName(scope)}?`, body: "The scope stays; it is no longer found by that id.", confirm: "Remove id", danger: true })) &&
                        run(() => del<SubjectDTO>(link(r.system)))
                      }
                    >
                      Remove
                    </Button>
                  )}
                </span>
              )
            }
          >
            {editing === r.system ? (
              <form
                className="mt-2 flex flex-wrap gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(() => put<SubjectDTO>(link(r.system), { externalId: value.trim() }));
                }}
              >
                <input className={`${inputCls} min-w-0 basis-full font-mono text-xs`} value={value} autoFocus aria-label={`${toolName(connectors, r.system)} ${r.idField}`} onChange={(e) => setValue(e.target.value)} />
                <Button variant="primary" disabled={busy || !value.trim() || value.trim() === id}>Save</Button>
                <Button type="button" onClick={() => setEditing(null)}>Cancel</Button>
              </form>
            ) : id ? (
              <div className="mt-1.5 font-mono text-xs break-all text-fg">{id}</div>
            ) : (
              <div className="mt-1.5 text-xs text-faint">Not set</div>
            )}
          </ListRow>
        );
      })}
    </List>
  );
}
